import { useState, useEffect, useCallback } from 'react';
import api from 'services/api';
import useEscapeClose from 'hooks/useEscapeClose';

const ETAT_COLORS = { neuf: '#2e7d32', bon: '#1565c0', usage: '#f57c00', defectueux: '#e53935', hors_service: '#9e9e9e' };
const STATUT_CMD = { en_attente: { label: 'En attente', color: '#f57c00' }, confirmee: { label: 'Confirmee', color: '#1565c0' }, livree: { label: 'Livree', color: '#2e7d32' }, annulee: { label: 'Annulee', color: '#e53935' } };
const CATEGORIES = ['Equipement agent', 'Videosurveillance', 'Alarme et detection', 'Controle acces', 'Detection incendie', 'Securite perimetrique', 'Signaletique', 'Formation et conseil', 'Telesurveillance'];

export default function EquipmentPage() {
  const [tab, setTab] = useState('stock');
  const [items, setItems] = useState([]);
  const [affectations, setAffectations] = useState([]);
  const [agents, setAgents] = useState([]);
  const [catalogue, setCatalogue] = useState([]);
  const [commandes, setCommandes] = useState([]);
  const [kpis, setKpis] = useState({ stock_total: 0, affectations_actives: 0, commandes_mois: 0, revenu_mois: 0 });
  const [catalogueProduits, setCatalogueProduits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [suggestions, setSuggestions] = useState([]);

  useEscapeClose(!!modal, () => setModal(null));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [eq, aff, ag, cat, cmd, st, cp] = await Promise.all([
        api.get('/equipements'),
        api.get('/equipements/affectations').catch(() => ({ data: [] })),
        api.get('/agents').catch(() => ({ data: [] })),
        api.get('/equipements/vente').catch(() => ({ data: [] })),
        api.get('/equipements/commandes').catch(() => ({ data: [] })),
        api.get('/equipements/stats').catch(() => ({ data: { stock_total: 0, affectations_actives: 0, commandes_mois: 0, revenu_mois: 0 } })),
        api.get('/catalogue').catch(() => ({ data: [] })),
      ]);
      setItems(eq.data);
      setAffectations(aff.data);
      setAgents(ag.data);
      setCatalogue(cat.data);
      setCommandes(cmd.data);
      setKpis(st.data);
      setCatalogueProduits(cp.data);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const closeModal = () => { setModal(null); setForm({}); setError(''); setSuggestions([]); };

  const handleNomChange = (val) => {
    set('nom', val);
    if (val.length >= 2) {
      const s = val.toLowerCase();
      setSuggestions(catalogueProduits.filter(p => p.designation.toLowerCase().includes(s) || p.code.toLowerCase().includes(s)).slice(0, 8));
    } else { setSuggestions([]); }
  };
  const selectSuggestion = (p) => {
    setForm(f => ({ ...f, nom: p.designation, prix_achat: Number(p.prix_achat) || f.prix_achat, categorie: p.categorie || f.categorie }));
    setSuggestions([]);
  };

  // ── STOCK MODAL ──
  const openNewStock = () => {
    setForm({ reference: '', nom: '', categorie: 'Equipement agent', description: '', etat: 'neuf', quantite_totale: 1, seuil_alerte: 5, prix_achat: '' });
    setModal('stock');
  };
  const handleStockSubmit = async (e) => {
    e.preventDefault(); setSaving(true); setError('');
    try {
      if (form.id) { await api.put(`/equipements/${form.id}`, form); }
      else { await api.post('/equipements', form); }
      closeModal(); load();
    } catch (err) { setError(err.response?.data?.error || 'Erreur'); }
    finally { setSaving(false); }
  };

  // ── AFFECTATION MODAL ──
  const openAffecter = () => {
    setForm({ equipement_id: '', agent_id: '', quantite: 1, date_retour_prevue: '', notes: '' });
    setModal('affecter');
  };
  const handleAffecterSubmit = async (e) => {
    e.preventDefault(); setSaving(true); setError('');
    try {
      await api.post('/equipements/affectations', form);
      closeModal(); load();
    } catch (err) { setError(err.response?.data?.error || 'Erreur'); }
    finally { setSaving(false); }
  };

  // ── RETOUR MODAL ──
  const openRetour = (aff) => {
    setForm({ retour_id: aff.id, etat_retour: 'bon', notes_retour: '' });
    setModal('retour');
  };
  const handleRetourSubmit = async (e) => {
    e.preventDefault(); setSaving(true); setError('');
    try {
      await api.put(`/equipements/affectations/${form.retour_id}/retour`, { etat_retour: form.etat_retour });
      closeModal(); load();
    } catch (err) { setError(err.response?.data?.error || 'Erreur'); }
    finally { setSaving(false); }
  };

  // ── COMMANDE MODAL ──
  const [cmdLignes, setCmdLignes] = useState([]);
  const openCommande = () => {
    setForm({ client_nom: '', client_tel: '', client_email: '', mode_paiement: 'Especes', notes: '' });
    setCmdLignes([{ catalogue_vente_id: '', produit_id: '', designation: '', quantite: 1, prix_unitaire: 0 }]);
    setModal('commande');
  };
  const addLigne = () => setCmdLignes(l => [...l, { catalogue_vente_id: '', produit_id: '', designation: '', quantite: 1, prix_unitaire: 0 }]);
  const removeLigne = (i) => setCmdLignes(l => l.filter((_, idx) => idx !== i));
  const setLigne = (i, k, v) => {
    setCmdLignes(lines => {
      const next = [...lines];
      next[i] = { ...next[i], [k]: v };
      if (k === 'produit_id' && v) {
        const art = catalogueProduits.find(c => c.id === v);
        if (art) { next[i].designation = `${art.code} — ${art.designation}`; next[i].prix_unitaire = Number(art.prix_unitaire_ht); }
      }
      return next;
    });
  };
  const cmdTotal = cmdLignes.reduce((s, l) => s + (Number(l.quantite) || 0) * (Number(l.prix_unitaire) || 0), 0);

  const handleCmdSubmit = async (e) => {
    e.preventDefault(); setSaving(true); setError('');
    try {
      await api.post('/equipements/commandes', { ...form, lignes: cmdLignes });
      closeModal(); setCmdLignes([]); load();
    } catch (err) { setError(err.response?.data?.error || 'Erreur'); }
    finally { setSaving(false); }
  };

  const handleStatutCmd = async (id, statut) => {
    try { await api.put(`/equipements/commandes/${id}/statut`, { statut }); load(); } catch {}
  };

  const filtered = items.filter(e => {
    if (!search) return true;
    const s = search.toLowerCase();
    return e.nom.toLowerCase().includes(s) || (e.reference || '').toLowerCase().includes(s);
  });

  return (
    <div>
      {/* KPIs */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-card-header"><span className="stat-card-label">Articles en stock</span></div>
          <div className="stat-card-value">{kpis.stock_total}</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-header"><span className="stat-card-label">Affectations actives</span></div>
          <div className="stat-card-value" style={{ color: 'var(--gss-primary)' }}>{kpis.affectations_actives}</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-header"><span className="stat-card-label">Commandes ce mois</span></div>
          <div className="stat-card-value">{kpis.commandes_mois}</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-header"><span className="stat-card-label">Revenu vente ce mois</span></div>
          <div className="stat-card-value" style={{ color: 'var(--gss-success)' }}>{Number(kpis.revenu_mois).toLocaleString()}</div>
          <div className="stat-card-sub">FCFA</div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <button className={`gss-btn ${tab === 'stock' ? 'gss-btn-primary' : ''}`} onClick={() => setTab('stock')}>Stock & Affectation</button>
        <button className={`gss-btn ${tab === 'vente' ? 'gss-btn-primary' : ''}`} onClick={() => setTab('vente')}>Vente Tiers</button>
      </div>

      {error && <div className="gss-alert gss-alert-error">{error}<button onClick={() => setError('')} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer' }}>&times;</button></div>}

      {/* ════════ ONGLET 1 — STOCK INTERNE ════════ */}
      {tab === 'stock' && (
        <>
          <div className="content-card">
            <div className="gss-toolbar">
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <div className="content-card-title">Stock interne</div>
                <input className="gss-input" placeholder="Rechercher..." value={search} onChange={e => setSearch(e.target.value)} style={{ width: 180 }} />
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="gss-btn gss-btn-primary" onClick={openAffecter}>Affecter equipement</button>
                <button className="gss-btn" onClick={openNewStock}>+ Nouvel article</button>
              </div>
            </div>

            {loading ? <p style={{ textAlign: 'center', padding: '2rem', color: 'var(--gss-text-light)' }}>Chargement...</p> : (
              <div className="gss-table-wrap">
                <table className="gss-table">
                  <thead><tr><th>Ref.</th><th>Designation</th><th>Categorie</th><th>Etat</th><th>Total</th><th>Disponible</th><th>Affecte</th><th>Seuil</th><th>Actions</th></tr></thead>
                  <tbody>
                    {filtered.map(eq => {
                      const alerte = eq.quantite_disponible < eq.seuil_alerte;
                      return (
                        <tr key={eq.id} style={alerte ? { background: '#FEF2F2' } : undefined}>
                          <td style={{ fontSize: '0.78rem', color: '#888' }}>{eq.reference || '-'}</td>
                          <td><strong>{eq.nom}</strong></td>
                          <td style={{ fontSize: '0.78rem' }}>{eq.categorie || '-'}</td>
                          <td>
                            <span className="gss-badge" style={{ background: (ETAT_COLORS[eq.etat] || '#999') + '20', color: ETAT_COLORS[eq.etat] }}>{eq.etat}</span>
                          </td>
                          <td>{eq.quantite_totale}</td>
                          <td style={{ fontWeight: 700, color: alerte ? '#DC2626' : 'var(--gss-success)' }}>{eq.quantite_disponible}</td>
                          <td>{eq.quantite_affectee}</td>
                          <td>{eq.seuil_alerte}</td>
                          <td>
                            <button className="gss-btn-sm" onClick={() => { setForm({ ...eq }); setModal('stock'); }}>Modifier</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Affectations actives */}
          <div className="content-card" style={{ marginTop: 16 }}>
            <div className="content-card-title">Affectations en cours</div>
            {affectations.filter(a => !a.date_retour_effective).length === 0 ? (
              <p style={{ textAlign: 'center', padding: '1.5rem', color: 'var(--gss-text-light)' }}>Aucune affectation active</p>
            ) : (
              <div className="gss-table-wrap">
                <table className="gss-table">
                  <thead><tr><th>Agent</th><th>Equipement</th><th>Qte</th><th>Date affectation</th><th>Retour prevu</th><th>Statut</th><th>Actions</th></tr></thead>
                  <tbody>
                    {affectations.filter(a => !a.date_retour_effective).map(a => {
                      const prevu = a.date_retour_prevue ? new Date(a.date_retour_prevue) : null;
                      const now = new Date();
                      const diffJ = prevu ? Math.ceil((prevu - now) / (1000 * 60 * 60 * 24)) : null;
                      const enRetard = diffJ !== null && diffJ < 0;
                      const bientot = diffJ !== null && diffJ >= 0 && diffJ <= 7;
                      return (
                        <tr key={a.id} style={enRetard ? { background: '#FEF2F2' } : bientot ? { background: '#FFFBEB' } : undefined}>
                          <td><strong>{a.agent_prenom} {a.agent_nom}</strong><div style={{ fontSize: '0.72rem', color: '#888' }}>{a.matricule}</div></td>
                          <td>{a.equipement_nom}</td>
                          <td>{a.quantite || 1}</td>
                          <td>{a.date_dotation?.split('T')[0]}</td>
                          <td>{prevu ? prevu.toISOString().split('T')[0] : '-'}</td>
                          <td>
                            {enRetard && <span className="gss-badge" style={{ background: '#DC262620', color: '#DC2626' }}>En retard ({Math.abs(diffJ)}j)</span>}
                            {bientot && <span className="gss-badge" style={{ background: '#F5970020', color: '#D97706' }}>Retour dans {diffJ}j</span>}
                            {!enRetard && !bientot && <span className="gss-badge" style={{ background: '#2e7d3220', color: '#2e7d32' }}>En cours</span>}
                          </td>
                          <td><button className="gss-btn-sm" style={{ color: '#2e7d32' }} onClick={() => openRetour(a)}>Enregistrer retour</button></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* ════════ ONGLET 2 — VENTE TIERS ════════ */}
      {tab === 'vente' && (
        <>
          <div className="content-card">
            <div className="gss-toolbar">
              <div className="content-card-title">Catalogue vente tiers</div>
              <button className="gss-btn gss-btn-primary" onClick={openCommande}>+ Nouvelle commande</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12, padding: '0 0 1rem' }}>
              {catalogue.map(c => (
                <div key={c.id} style={{ border: '1px solid #EEE', borderRadius: 10, padding: 16, background: '#FAFAFA' }}>
                  <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>{c.designation}</div>
                  {c.description_commerciale && <div style={{ fontSize: 12, color: '#888', marginBottom: 8 }}>{c.description_commerciale}</div>}
                  <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--gss-red)' }}>{Number(c.prix_vente).toLocaleString()} FCFA</div>
                  <div style={{ fontSize: 11, color: c.stock_vente < 3 ? '#DC2626' : '#888', marginTop: 4 }}>Stock : {c.stock_vente}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Commandes */}
          <div className="content-card" style={{ marginTop: 16 }}>
            <div className="content-card-title">Commandes</div>
            {commandes.length === 0 ? (
              <p style={{ textAlign: 'center', padding: '1.5rem', color: 'var(--gss-text-light)' }}>Aucune commande</p>
            ) : (
              <div className="gss-table-wrap">
                <table className="gss-table">
                  <thead><tr><th>Date</th><th>Client</th><th>Total</th><th>Paiement</th><th>Statut</th><th>Actions</th></tr></thead>
                  <tbody>
                    {commandes.map(c => {
                      const st = STATUT_CMD[c.statut] || { label: c.statut, color: '#999' };
                      return (
                        <tr key={c.id}>
                          <td>{c.date_commande?.split('T')[0]}</td>
                          <td><strong>{c.client_nom}</strong>{c.client_tel && <div style={{ fontSize: '0.72rem', color: '#888' }}>{c.client_tel}</div>}</td>
                          <td style={{ fontWeight: 700 }}>{Number(c.total_commande).toLocaleString()} F</td>
                          <td>{c.mode_paiement || '-'}</td>
                          <td><span className="gss-badge" style={{ background: st.color + '20', color: st.color }}>{st.label}</span></td>
                          <td style={{ display: 'flex', gap: 4 }}>
                            {c.statut === 'en_attente' && <button className="gss-btn-sm" style={{ color: '#1565c0' }} onClick={() => handleStatutCmd(c.id, 'confirmee')}>Confirmer</button>}
                            {c.statut === 'confirmee' && <button className="gss-btn-sm" style={{ color: '#2e7d32' }} onClick={() => handleStatutCmd(c.id, 'livree')}>Livrer</button>}
                            {(c.statut === 'confirmee' || c.statut === 'livree') && (
                              <a href={`${process.env.REACT_APP_API_URL || '/api'}/equipements/commandes/${c.id}/facture-pdf`} target="_blank" rel="noreferrer" className="gss-btn-sm">Facture PDF</a>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* ════════ MODALS ════════ */}

      {/* Modal Stock */}
      {modal === 'stock' && (
        <div className="gss-modal-overlay" onClick={closeModal}>
          <div className="gss-modal gss-modal-lg" onClick={e => e.stopPropagation()}>
            <div className="gss-modal-header">
              <h2>{form.id ? 'Modifier equipement' : 'Nouvel equipement'}</h2>
              <button className="gss-modal-close" onClick={closeModal}>&times;</button>
            </div>
            <form onSubmit={handleStockSubmit}>
              <div style={{ padding: '1.5rem' }}>
                <div className="gss-form-grid">
                  <div className="gss-field"><label>Reference</label><input className="gss-input" value={form.reference || ''} onChange={e => set('reference', e.target.value)} placeholder="GLD-EQ-010" /></div>
                  <div className="gss-field" style={{ position: 'relative' }}>
                    <label>Designation *</label>
                    <input className="gss-input" required value={form.nom || ''} onChange={e => handleNomChange(e.target.value)} onBlur={() => setTimeout(() => setSuggestions([]), 200)} autoComplete="off" placeholder="Tapez pour rechercher dans le catalogue..." />
                    {suggestions.length > 0 && (
                      <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #ddd', borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 50, maxHeight: 220, overflowY: 'auto' }}>
                        {suggestions.map(p => (
                          <div key={p.id} style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #f0f0f0', fontSize: 13 }} onMouseDown={() => selectSuggestion(p)}>
                            <div style={{ fontWeight: 600 }}>{p.designation}</div>
                            <div style={{ fontSize: 11, color: '#888' }}>{p.code} — {p.categorie} — {Number(p.prix_achat || 0).toLocaleString()} F achat</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="gss-field"><label>Categorie</label>
                    <select className="gss-input" value={form.categorie || ''} onChange={e => set('categorie', e.target.value)}>
                      <option value="">-- Choisir --</option>
                      {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div className="gss-field"><label>Etat</label>
                    <select className="gss-input" value={form.etat || 'neuf'} onChange={e => set('etat', e.target.value)}>
                      <option value="neuf">Neuf</option><option value="bon">Bon</option><option value="usage">Usage</option>
                      <option value="defectueux">Defectueux</option><option value="hors_service">Hors service</option>
                    </select>
                  </div>
                  <div className="gss-field"><label>Quantite totale</label><input className="gss-input" type="number" min="0" value={form.quantite_totale || ''} onChange={e => set('quantite_totale', e.target.value)} /></div>
                  <div className="gss-field"><label>Seuil alerte</label><input className="gss-input" type="number" min="0" value={form.seuil_alerte || ''} onChange={e => set('seuil_alerte', e.target.value)} /></div>
                  <div className="gss-field"><label>Prix achat (FCFA)</label><input className="gss-input" type="number" min="0" value={form.prix_achat || ''} onChange={e => set('prix_achat', e.target.value)} /></div>
                  <div className="gss-field gss-field-full"><label>Description</label><input className="gss-input" value={form.description || ''} onChange={e => set('description', e.target.value)} /></div>
                </div>
              </div>
              <div className="gss-modal-actions">
                <button type="button" className="gss-btn-cancel" onClick={closeModal}>Annuler</button>
                <button type="submit" className="gss-btn-deploy" disabled={saving}>{saving ? 'Enregistrement...' : (form.id ? 'Enregistrer' : 'Creer')}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Affectation */}
      {modal === 'affecter' && (
        <div className="gss-modal-overlay" onClick={closeModal}>
          <div className="gss-modal gss-modal-lg" onClick={e => e.stopPropagation()}>
            <div className="gss-modal-header">
              <h2>Affecter un equipement</h2>
              <button className="gss-modal-close" onClick={closeModal}>&times;</button>
            </div>
            <form onSubmit={handleAffecterSubmit}>
              <div style={{ padding: '1.5rem' }}>
                <div className="gss-form-grid">
                  <div className="gss-field"><label>Equipement *</label>
                    <select className="gss-input" required value={form.equipement_id} onChange={e => set('equipement_id', e.target.value)}>
                      <option value="">-- Choisir --</option>
                      {items.filter(e => e.quantite_disponible > 0).map(e => (
                        <option key={e.id} value={e.id}>{e.nom} ({e.quantite_disponible} dispo)</option>
                      ))}
                    </select>
                  </div>
                  <div className="gss-field"><label>Agent *</label>
                    <select className="gss-input" required value={form.agent_id} onChange={e => set('agent_id', e.target.value)}>
                      <option value="">-- Choisir --</option>
                      {agents.map(a => <option key={a.id} value={a.id}>{a.prenom} {a.nom} ({a.matricule})</option>)}
                    </select>
                  </div>
                  <div className="gss-field"><label>Quantite</label><input className="gss-input" type="number" min="1" value={form.quantite} onChange={e => set('quantite', e.target.value)} /></div>
                  <div className="gss-field"><label>Date retour prevue</label><input className="gss-input" type="date" value={form.date_retour_prevue} onChange={e => set('date_retour_prevue', e.target.value)} /></div>
                  <div className="gss-field gss-field-full"><label>Notes</label><input className="gss-input" value={form.notes} onChange={e => set('notes', e.target.value)} /></div>
                </div>
              </div>
              <div className="gss-modal-actions">
                <button type="button" className="gss-btn-cancel" onClick={closeModal}>Annuler</button>
                <button type="submit" className="gss-btn-deploy" disabled={saving}>{saving ? 'Enregistrement...' : 'Affecter'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Retour */}
      {modal === 'retour' && (
        <div className="gss-modal-overlay" onClick={closeModal}>
          <div className="gss-modal" onClick={e => e.stopPropagation()}>
            <div className="gss-modal-header">
              <h2>Enregistrer retour</h2>
              <button className="gss-modal-close" onClick={closeModal}>&times;</button>
            </div>
            <form onSubmit={handleRetourSubmit}>
              <div style={{ padding: '1.5rem' }}>
                <div className="gss-form-grid">
                  <div className="gss-field"><label>Etat au retour *</label>
                    <select className="gss-input" required value={form.etat_retour} onChange={e => set('etat_retour', e.target.value)}>
                      <option value="bon">Bon etat</option>
                      <option value="usage">Use / endommage</option>
                      <option value="defectueux">Defectueux</option>
                      <option value="perdu">Perdu</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="gss-modal-actions">
                <button type="button" className="gss-btn-cancel" onClick={closeModal}>Annuler</button>
                <button type="submit" className="gss-btn-deploy" disabled={saving}>{saving ? 'Enregistrement...' : 'Confirmer retour'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Commande */}
      {modal === 'commande' && (
        <div className="gss-modal-overlay" onClick={closeModal}>
          <div className="gss-modal gss-modal-lg" onClick={e => e.stopPropagation()} style={{ maxWidth: 700 }}>
            <div className="gss-modal-header">
              <h2>Nouvelle commande</h2>
              <button className="gss-modal-close" onClick={closeModal}>&times;</button>
            </div>
            <form onSubmit={handleCmdSubmit}>
              <div style={{ padding: '1.5rem' }}>
                <div className="gss-modal-section"><span>CLIENT</span></div>
                <div className="gss-form-grid">
                  <div className="gss-field"><label>Nom client *</label><input className="gss-input" required value={form.client_nom} onChange={e => set('client_nom', e.target.value)} /></div>
                  <div className="gss-field"><label>Telephone</label><input className="gss-input" value={form.client_tel} onChange={e => set('client_tel', e.target.value)} /></div>
                  <div className="gss-field"><label>Email</label><input className="gss-input" type="email" value={form.client_email} onChange={e => set('client_email', e.target.value)} /></div>
                  <div className="gss-field"><label>Mode paiement</label>
                    <select className="gss-input" value={form.mode_paiement} onChange={e => set('mode_paiement', e.target.value)}>
                      <option value="Especes">Especes</option><option value="Mobile Money">Mobile Money</option><option value="Virement">Virement</option><option value="Cheque">Cheque</option>
                    </select>
                  </div>
                </div>

                <div className="gss-modal-section" style={{ marginTop: 16 }}><span>ARTICLES</span></div>
                {cmdLignes.map((l, i) => (
                  <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginBottom: 8 }}>
                    <div style={{ flex: 2 }}>
                      {i === 0 && <label style={{ fontSize: 12, fontWeight: 600 }}>Produit catalogue</label>}
                      <select className="gss-input" value={l.produit_id} onChange={e => setLigne(i, 'produit_id', e.target.value)}>
                        <option value="">-- Produit catalogue --</option>
                        {catalogueProduits.filter(c => c.actif !== false).map(c => (
                          <option key={c.id} value={c.id}>{c.code} — {c.designation} ({Number(c.prix_unitaire_ht).toLocaleString()} F)</option>
                        ))}
                      </select>
                    </div>
                    <div style={{ width: 70 }}>
                      {i === 0 && <label style={{ fontSize: 12, fontWeight: 600 }}>Qte</label>}
                      <input className="gss-input" type="number" min="1" value={l.quantite} onChange={e => setLigne(i, 'quantite', e.target.value)} />
                    </div>
                    <div style={{ width: 100 }}>
                      {i === 0 && <label style={{ fontSize: 12, fontWeight: 600 }}>P.U.</label>}
                      <input className="gss-input" type="number" value={l.prix_unitaire} readOnly style={{ background: '#f5f5f5' }} />
                    </div>
                    <div style={{ width: 100, textAlign: 'right', fontWeight: 700, fontSize: 13, paddingBottom: 8 }}>
                      {((Number(l.quantite) || 0) * (Number(l.prix_unitaire) || 0)).toLocaleString()} F
                    </div>
                    {cmdLignes.length > 1 && <button type="button" style={{ background: 'none', border: 'none', color: '#DC2626', cursor: 'pointer', fontSize: 18, paddingBottom: 6 }} onClick={() => removeLigne(i)}>&times;</button>}
                  </div>
                ))}
                <button type="button" className="gss-btn" onClick={addLigne} style={{ marginTop: 4 }}>+ Ajouter article</button>

                <div style={{ marginTop: 16, padding: 12, background: '#F8F8F8', borderRadius: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontWeight: 700, fontSize: 15 }}>TOTAL</span>
                  <span style={{ fontWeight: 800, fontSize: 18, color: 'var(--gss-red)' }}>{cmdTotal.toLocaleString()} FCFA</span>
                </div>
                <div style={{ fontSize: 11, color: '#888', fontStyle: 'italic', marginTop: 4 }}>Exonere de TVA — Regime simplifie</div>
              </div>
              <div className="gss-modal-actions">
                <button type="button" className="gss-btn-cancel" onClick={closeModal}>Annuler</button>
                <button type="submit" className="gss-btn-deploy" disabled={saving || cmdTotal === 0}>{saving ? 'Enregistrement...' : 'Confirmer et generer facture'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
